import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Cloud, CheckCircle, AlertCircle, Loader, Download, RefreshCw } from 'lucide-react';

function AdminLocaldataSync() {
    const [logs, setLogs] = useState([]);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const snap = await getDocs(query(
                collection(db, 'syncLogs'),
                where('type', '==', 'localdata-sync'),
                orderBy('finishedAt', 'desc'),
                limit(20)
            ));
            setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        }
    };

    const runSync = async (opts) => {
        if (!window.confirm(opts.dryRun
            ? '시험 동기화를 실행할까요?'
            : '전국 치과기공소 동기화를 시작합니다.\n분할 처리되어 약 5~10분 소요됩니다.\n계속할까요?')) return;
        setRunning(true);
        setResult(null);
        setError(null);
        setProgress('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const sync = httpsCallable(fns, 'localdataSyncLabs', { timeout: 540000 });

            if (opts.dryRun) {
                const res = await sync({ ...opts, maxPages: 2 });
                setResult(res.data);
            } else {
                const totals = { fetched: 0, written: 0, errors: 0 };
                let startPage = 1;
                let finished = false;
                let safety = 100;
                let totalCalls = 0;
                while (!finished && safety-- > 0) {
                    totalCalls++;
                    setProgress(`치과기공소 · 페이지 ${startPage}~${startPage + 4} (호출 ${totalCalls}회 · 누적 ${totals.written}건)`);
                    const res = await sync({ startPage, maxPages: 5, pageSize: 100 });
                    const s = res.data?.stats || {};
                    totals.fetched += s.fetched || 0;
                    totals.written += s.written || 0;
                    totals.errors += s.errors || 0;
                    finished = !!s.finished;
                    startPage = (s.lastPage || startPage) + 1;
                }
                setResult({ stats: totals });
            }
            await load();
        } catch (e) {
            console.error('동기화 실패:', e);
            setError(e?.message || '동기화 실패');
        } finally {
            setRunning(false);
            setProgress('');
        }
    };

    return (
        <div style={S.container}>
            <div style={S.header}>
                <h1 style={S.title}><Cloud size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> LOCALDATA 치과기공소 동기화</h1>
                <p style={S.subtitle}>지방행정인허가데이터(LOCALDATA)에서 전국 치과기공소 정보를 자동 가져옵니다</p>
            </div>

            <div style={S.startCard}>
                <h2 style={S.startTitle}>동기화 실행</h2>
                <p style={S.startDesc}>
                    전국 치과기공소를 <code>labDirectories</code> 컬렉션에 저장합니다.<br />
                    기존 가입·구독 정보는 보존됩니다.<br />
                    <strong>예상 소요:</strong> 5~10분 / <strong>예상 결과:</strong> 약 4,000~5,000개
                </p>
                <div style={S.btnRow}>
                    <button
                        onClick={() => runSync({ dryRun: true })}
                        disabled={running}
                        style={{ ...S.btn, background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1' }}
                    >
                        {running ? <Loader size={14} className="spin" /> : <Download size={14} />} 시험 (2페이지만)
                    </button>
                    <button
                        onClick={() => runSync({ dryRun: false })}
                        disabled={running}
                        style={S.btn}
                    >
                        {running ? <><Loader size={14} className="spin" /> 동기화 중...</> : <><RefreshCw size={14} /> 전체 동기화</>}
                    </button>
                </div>

                {running && (
                    <div style={S.progress}>
                        ⏳ {progress || '동기화 진행 중...'}<br />
                        <span style={{ fontSize: 11, opacity: 0.7 }}>페이지를 닫지 마세요. 완료까지 5~10분 소요됩니다.</span>
                    </div>
                )}

                {result && (
                    <div style={S.success}>
                        <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        <strong>동기화 완료</strong><br />
                        총 {result.stats?.fetched || 0}건 조회 · 저장 {result.stats?.written || 0}건 · 오류 {result.stats?.errors || 0}건
                    </div>
                )}

                {error && (
                    <div style={S.error}>
                        <AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        {error}
                    </div>
                )}
            </div>

            <div style={S.guideCard}>
                <h3 style={S.guideTitle}>📋 사전 준비</h3>
                <ol style={S.guideList}>
                    <li>공공데이터포털 (data.go.kr) 가입·로그인</li>
                    <li>"지방행정인허가데이터" 또는 "치과기공소" 검색 → 활용신청</li>
                    <li>승인 후 일반 인증키(Decoded) 복사</li>
                    <li>터미널: <code style={S.code}>firebase functions:secrets:set LOCALDATA_API_KEY</code></li>
                    <li>재배포: <code style={S.code}>firebase deploy --only functions:localdataSyncLabs</code></li>
                </ol>
            </div>

            <div style={S.logsCard}>
                <h3 style={S.logsTitle}>최근 동기화 로그</h3>
                {logs.length === 0 ? (
                    <p style={S.empty}>아직 실행된 동기화가 없습니다</p>
                ) : (
                    <div style={S.logsList}>
                        {logs.map(log => (
                            <div key={log.id} style={S.logItem}>
                                <div style={S.logTime}>{formatDate(log.finishedAt)}</div>
                                <div style={S.logStats}>
                                    조회 {log.stats?.fetched || 0}건 · 저장 {log.stats?.written || 0}건
                                    {log.stats?.errors > 0 && <span style={{ color: '#ef4444' }}> · 오류 {log.stats.errors}건</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const formatDate = (ts) => {
    if (!ts) return '-';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const S = {
    container: { padding: 24, maxWidth: 960, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 900, color: '#0f172a', margin: 0 },
    subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
    startCard: { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid #e2e8f0' },
    startTitle: { fontSize: 18, fontWeight: 800, marginBottom: 8 },
    startDesc: { fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 18 },
    btnRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    progress: { marginTop: 14, padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' },
    success: { marginTop: 14, padding: 12, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, fontSize: 13, color: '#065f46', lineHeight: 1.7 },
    error: { marginTop: 14, padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' },
    guideCard: { background: '#f0f9ff', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid #bae6fd' },
    guideTitle: { fontSize: 15, fontWeight: 800, color: '#0c4a6e', marginBottom: 10 },
    guideList: { paddingLeft: 24, fontSize: 13, color: '#0c4a6e', lineHeight: 2 },
    code: { background: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', color: '#6366f1' },
    logsCard: { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' },
    logsTitle: { fontSize: 15, fontWeight: 800, marginBottom: 12 },
    empty: { fontSize: 13, color: '#94a3b8', padding: '20px 0' },
    logsList: { display: 'flex', flexDirection: 'column', gap: 8 },
    logItem: { padding: 10, background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logTime: { fontSize: 12, color: '#64748b' },
    logStats: { fontSize: 13, fontWeight: 600, color: '#0f172a' },
};

export default AdminLocaldataSync;
